import type { BasisValue } from './types';

// Literal calls let the extractor collect keys that the screen selects dynamically through `peopleWord`.
const qualityKeys = (t: (key: string) => string) => ({
  people: t('People'),
  peopleOverTime: t('People over time'),
  peopleByAddress: t('People on each of your addresses'),
  websitePeople: t('Website people'),
  manyPeople: t('{{n}} people'),
  onePerson: t('1 person'),
  peopleSource: t('Each person counts once, under the source that last brought them.'),
  peopleSourceCountry: t(
    'Each person counts once, under the source that last brought them, by the country of their latest visit.',
  ),
  noPeople: t('No people counted here {{period}}.'),
  noPeopleBefore: t('There were no people {{previous}}.'),
  peopleUnavailable: t('People counts are unavailable for this selection by page or over time.'),
  peopleSources: t('Where people come from'),
  estimate: t(
    'People are estimated. We link visits to the same verified account where possible. Without that link, each browser counts separately. A shared browser can represent more than one person.',
  ),
  visits: t('Visits are tracking sessions. One person can make several visits.'),
  overlap: t('A person can appear in more than one row.'),
  updates: t('Numbers update every few minutes.'),
  credited: t(
    'People are credited once per person, under the source that last brought them. Visits are credited once per session.',
  ),
  arrival: t(
    'People and visits count where they arrived. Pages viewed and time count under the source that last brought them.',
  ),
});
const QUALITY_KEYS = qualityKeys((key) => key);

/**
 * The overview card's muted count line for the visitors' basis and the number of selected hosts, as English keys in
 * reading order; empty when the line is left out.
 */
export const countLine = (people: BasisValue, selectedHosts: number): string[] => {
  if (people === 'credit') return [QUALITY_KEYS.credited];
  if (people !== 'none' && people !== 'arrival') return [];
  const line: string[] = [];
  if (selectedHosts >= 2) line.push('A person can appear in more than one row.');
  if (people === 'arrival') line.push(QUALITY_KEYS.arrival);
  return line;
};

/** The page and PDF footnote for the visitors' basis, as English keys in reading order. */
export const footnote = (_people: BasisValue): string[] => {
  const { estimate, visits, overlap, updates } = QUALITY_KEYS;
  return [estimate, visits, overlap, updates];
};

const PAGE_PLACES = ['your website', 'these pages', 'this page', 'this landing page'];
const LINK_PLACES = ['these links', 'this link', 'your own links', "this affiliate's links", "this campaign's links"];

const PEOPLE_WORDS = new Map<string, string>([
  ['Visitors', QUALITY_KEYS.people],
  ['Visitors over time', QUALITY_KEYS.peopleOverTime],
  ['Visitors on each of your addresses', QUALITY_KEYS.peopleByAddress],
  ['Where visitors come from', QUALITY_KEYS.peopleSources],
  ['Website visitors', QUALITY_KEYS.websitePeople],
  ['{{n}} visitors', QUALITY_KEYS.manyPeople],
  ['1 visitor', QUALITY_KEYS.onePerson],
  ...PAGE_PLACES.flatMap((place): [string, string][] => [
    [`{{visitors}} people visited ${place} {{period}}.`, `{{visitors}} people visited ${place} {{period}}.`],
    [`1 person visited ${place} {{period}}.`, `1 person visited ${place} {{period}}.`],
  ]),
  ...LINK_PLACES.flatMap((place): [string, string][] => [
    [`{{visitors}} people came through ${place} {{period}}.`, `{{visitors}} people came through ${place} {{period}}.`],
    [`1 person came through ${place} {{period}}.`, `1 person came through ${place} {{period}}.`],
  ]),
  ...['your website', 'these pages', 'this page'].map((place): [string, string] => [
    `Different people who opened ${place}`,
    `Different people who opened ${place}`,
  ]),
  ['Each visitor counts once, under the source that last brought them.', QUALITY_KEYS.peopleSource],
  [
    'Each visitor counts once, under the source that last brought them, by the country of their latest visit.',
    QUALITY_KEYS.peopleSourceCountry,
  ],
  ['No visitors counted here {{period}}.', QUALITY_KEYS.noPeople],
  ['There were no visitors {{previous}}.', QUALITY_KEYS.noPeopleBefore],
  ['How many people came, day by day', 'How many people came, day by day'],
  ['How many people came, hour by hour', 'How many people came, hour by hour'],
  ['Visitor counts are unavailable for this selection by page or over time.', QUALITY_KEYS.peopleUnavailable],
]);

/** A legacy string that counts visitors, as quality mode words it for people; other strings unchanged. */
export const peopleWord = (legacy: string, quality: boolean): string => (quality && PEOPLE_WORDS.get(legacy)) || legacy;
